import {
  InputProps,
  TextField,
  TextFieldProps,
  TextFieldVariants,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import createPalette from "@mui/material/styles/createPalette";
import React from "react";
import { vars } from "../Variables";
import { useField, useFormikContext } from "formik";

const { primaryFont, primaryColor, errorColor, greyLight, grey, greyDark } =
  vars;

declare module "@mui/material/styles" {
  interface Theme {}
  // allow configuration using `createTheme`
  interface ThemeOptions {}
}

const customPalette = createPalette({
  primary: {
    main: primaryColor,
  },
  grey: {
    "100": greyLight,
    "200": grey,
    "300": greyDark,
  },
  error: {
    main: errorColor,
  },
});

const theme = createTheme({
  typography: {
    fontFamily: primaryFont,
  },
  components: {
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: "183%",
          color: customPalette.grey[300],
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {},
        root: {
          "&:hover:not(.Mui-disabled, .Mui-error)::before": {
            borderColor: customPalette.grey[200],
            borderWidth: 1,
          },

          "&:after": {
            borderColor: customPalette.primary.main,
          },
          "&:before": {
            borderColor: customPalette.grey[200],
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: customPalette.error.main,
        },
        root: {
          fontWeight: 400,
          fontSize: "1rem",
          color: customPalette?.grey[100],

          "&.Mui-focused": {
            color: customPalette?.grey[100],
          },
        },
      },
    },
  },
});

export interface InputBoxProps {
  id?: string;
  rows?: number;
  sx?: Object;
  label?: string;
  error?: boolean;
  maxRows?: number;
  className?: string;
  fullWidth?: boolean;
  focused?: boolean;
  multiline?: boolean;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  defaultValue?: string;
  variant?: TextFieldVariants;
  type?: string;
  InputProps?: InputProps;
  color?: "error" | "primary" | "secondary" | "info" | "success" | "warning";
  value?: any;
  name?: any;
  [x: string]: any;
}

export const InputBox: React.FC<InputBoxProps & TextFieldProps> = (props) => {
  const {
    className,
    onChange,
    label,
    variant = "standard",
    id,
    fullWidth = false,
    helperText,
    sx,
    multiline = false,
    maxRows = 0,
    rows = 0,
    error = false,
    placeholder,
    defaultValue,
    type = "text",
    InputProps,
    color = "primary",
    required = false,
    disabled = false,
    name,
    value,
    autoComplete = "off",
  } = props;
  const [field, meta, helpers] = useField({ name, type });
  const { submitCount } = useFormikContext();
  const showError = (submitCount > 0 || meta.touched) && meta.error;
  return (
    <ThemeProvider theme={theme}>
      <TextField
        {...field}
        {...props}
        id={id}
        type={type}
        color={color}
        error={error || (meta.touched && !!meta.error)}
        sx={sx}
        label={label}
        variant={variant}
        spellCheck={props.spellCheck || false}
        disabled={disabled}
        required={required}
        autoComplete={autoComplete}
        onChange={(e) => {
          !meta.touched && helpers.setTouched(true);
          onChange?.(e);
          field?.onChange(e);
        }}
        fullWidth={fullWidth}
        className={`${className} ${showError ? "false" : ""}`}
        multiline={multiline}
        helperText={helperText || (meta.touched ? meta.error : "")}
        InputProps={InputProps}
        placeholder={placeholder}
        rows={multiline ? rows : 0}
        defaultValue={defaultValue}
        maxRows={multiline ? maxRows : 0}
        value={(value || field.value) || ""}
      />
    </ThemeProvider>
  );
};
